import { yupResolver } from '@hookform/resolvers/yup';
import classNames from 'classnames';
import { useTranslation } from 'next-i18next';
import Dropzone from 'react-dropzone';
import {
  Controller,
  FormProvider,
  SubmitHandler,
  useForm,
} from 'react-hook-form';

import { useChatInput } from '@/shared/components/atoms/ChatInput/logic';
import DynamicTextArea from '@/shared/components/atoms/DynamicTextArea';
import FileCard from '@/shared/components/atoms/FileCard';

import PlusIcon from '~/images/shared/Plus.svg';
import SendIcon from '~/images/shared/Send.svg';

const ChatInput: React.FC<ChatInputProps> = ({ handleSubmit, isDisabled }) => {
  const { t } = useTranslation('ai');

  const { chatInputSchema, supportedFiles } = useChatInput();

  const formMethods = useForm<ChatInputForm>({
    defaultValues: {
      message: '',
    },
    resolver: yupResolver(chatInputSchema),
    mode: 'all',
  });

  const onSubmit: SubmitHandler<ChatInputForm> = (values) => {
    handleSubmit(values);
    formMethods.reset();
  };

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={formMethods.handleSubmit(onSubmit)}>
        <Controller
          name='file'
          control={formMethods.control}
          render={({ field: { onChange, value } }) => (
            <Dropzone
              noDrag
              noClick
              multiple={false}
              accept={supportedFiles}
              onDrop={(acceptedFiles, rejectedFiles) =>
                onChange(
                  // add rejected files to state, but an error will be shown and they cannot be submitted
                  rejectedFiles?.length
                    ? rejectedFiles[0].file
                    : acceptedFiles[0]
                )
              }
            >
              {({ getRootProps, getInputProps, open }) => (
                <div className='flex items-end gap-[10px]'>
                  <div
                    {...getRootProps({
                      className:
                        'relative w-full rounded-xl leading-none justify-center',
                      style: {
                        boxShadow:
                          '0px 0px 20px 0px #12386233, 2px 2px 20px 0px #8638e54d, -2px -2px 20px 0px #3783d84d',
                      },
                    })}
                  >
                    {value && (
                      <div className='mx-16 mt-16 mb-0'>
                        <FileCard
                          name={value.name}
                          size={value.size}
                          error={formMethods.formState.errors?.file?.message}
                          handleRemove={() => onChange(undefined)}
                        />
                      </div>
                    )}
                    <div className='flex items-end'>
                      <div
                        className='cursor-pointer pb-16 ltr:pl-16 rtl:pr-16'
                        onClick={open}
                      >
                        <input {...getInputProps()} />
                        <PlusIcon />
                      </div>
                      <DynamicTextArea
                        disabled={isDisabled}
                        bordered={false}
                        id='message'
                        padding='py-12'
                        placeholder={t('message-nugz')}
                        className='min-h-[50px] flex-grow scrollbar-thin'
                        // submit on enter press
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            formMethods.handleSubmit(onSubmit)();
                          }
                        }}
                      />
                    </div>
                  </div>
                  <button
                    disabled={isDisabled}
                    type='submit'
                    className={classNames(
                      'flex h-48 w-48 items-center justify-center rounded-xl',
                      !formMethods.formState.isValid &&
                        'pointer-events-auto cursor-not-allowed opacity-5'
                    )}
                    style={{
                      background:
                        'linear-gradient(135deg, #32A4C7 0%, #3432C7 50%, #B632C7 100%)',
                    }}
                  >
                    <SendIcon width='24' height='24' />
                  </button>
                </div>
              )}
            </Dropzone>
          )}
        />
      </form>
    </FormProvider>
  );
};

export default ChatInput;
